import { defineSettingsScreen } from '@heidi/runtime/shell';

/**
 * The five Comms settings screen bodies.
 *
 * Each `id` is the settings route slug (`ROUTE_TO_SLUG['/settings/comms']` and
 * its four siblings), which is what lets a package serve existing settings
 * screens with the sidebar and the IA untouched: the loader resolves each route
 * to its contribution and falls back to the app's own importer for every route
 * no package claims.
 *
 * `section: 'comms'` is measured, not chosen — every one of the five renders
 * `group=` the same "Comms" message and the sidebar lists them under that group
 * (`SETTINGS_SECTION_SIDEBAR_LABELS.comms` records the label source).
 *
 * `order` runs 0-4 inside the section's `[0, 999]` band, in the order
 * `useSettingsList` renders the group today. It is inert — screens are served by
 * id and the sidebar is hand-written — so this claims no position anyone else
 * has to renumber around; it is recorded in the order it is, rather than all at
 * 0, because five screens in one section is the first case where the band has
 * anything to say.
 *
 * `builds: 'all'`: measured. Comms is cloud telephony and three of this
 * domain's flags are `cloud-only` for that reason, but the SCREENS are not
 * build-gated — `useSettingsList` renders the group under the same
 * `IS_CALLS_ENABLED() && callsOnboarded` condition on every profile — so a
 * narrower value would withhold from the on-prem shell a screen the appliance
 * resolves today.
 *
 * The thunks name the exports rather than relying on a default one: these are
 * named exports like every other component in this package, and
 * `ContributionLoadThunk` wants `{ default }`. The mapping is where the two
 * conventions meet, and it keeps the modules free of a default export nothing
 * else would use. Each thunk points at the SCREEN MODULE, never at the `.` door
 * — the door also carries the EHR integrations tree, the reminders tab and the
 * voice-language surface, and a contribution is the one consumer that can name
 * the module directly.
 */
export const commsSettingsScreen = defineSettingsScreen({
  id: 'comms',
  section: 'comms',
  order: 0,
  load: () =>
    import('../ui/settings/CommsSettings').then((module) => ({
      default: module.CommsSettings,
    })),
  builds: 'all',
});

export const commsDataManagementSettingsScreen = defineSettingsScreen({
  id: 'comms-data-management',
  section: 'comms',
  order: 1,
  load: () =>
    import('../ui/settings/CommsDataManagementSettings').then((module) => ({
      default: module.CommsDataManagementSettings,
    })),
  builds: 'all',
});

export const commsSummariesSettingsScreen = defineSettingsScreen({
  id: 'summaries',
  section: 'comms',
  order: 2,
  load: () =>
    import('../ui/settings/SummariesSettings').then((module) => ({
      default: module.SummariesSettings,
    })),
  builds: 'all',
});

export const commsBlockedNumbersSettingsScreen = defineSettingsScreen({
  id: 'blocked-numbers',
  section: 'comms',
  order: 3,
  load: () =>
    import('../ui/settings/BlockedNumbersSettings').then((module) => ({
      default: module.BlockedNumbersSettings,
    })),
  builds: 'all',
});

export const commsIntegrationsSettingsScreen = defineSettingsScreen({
  id: 'comms-integrations',
  section: 'comms',
  order: 4,
  load: () =>
    import('../ui/settings/CommsIntegrationsSettings').then((module) => ({
      default: module.CommsIntegrationsSettings,
    })),
  builds: 'all',
});
